import { Badge, Button, Card, ListGroup, Modal, Spinner, ToggleButton } from "react-bootstrap"
import { useAxios } from "./AxiosContext"
import { useEffect, useState } from "react"
import { Temporal } from "@js-temporal/polyfill"
import { Bank, BankId, useBanks } from "./BankContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLink, faPiggyBank, faWallet } from "@fortawesome/free-solid-svg-icons"

interface ConnectionDetails {
  bankId: string
  accounts: Record<AccountId, Account>
  activatedAt?: string
  expiresAt?: string
}

type ConnectionId = string

interface Account {
  iban: string
  ownerName: string
  displayName: string
  currency: string
  featureConfig: FeatureConfig
}

interface FeatureConfig {
  spendingLog: boolean
  savings: boolean
}

type AccountId = string


export const Accounts: React.FC = () => {

  const [showBankSelection, setShowBankSelection] = useState(false)

  const axios = useAxios()

  const banks = useBanks()

  const [redirecting, setRedirecting] = useState(false)

  const connect = async (bankId: BankId) => {
    try {
      setRedirecting(true)
      const response = await axios.post(`/v1/connections/init`, { bankId })

      if (response.data.redirectUri) {
        window.open(response.data.redirectUri, "_blank")
        setRedirecting(false)
      } else {
        console.log("No redirect URL in response")
        setRedirecting(false)
      }
    } catch (error) {
      console.error("Error connecting to bank", error)
      setRedirecting(false)
    }
  }

  return (
    <div className="py-3">
      <h3>Accounts</h3>
      <Modal show={showBankSelection} onHide={() => setShowBankSelection(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Connect to bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Choose a bank to connect to:</p>
          {banks ?
            <ListGroup>
              {Object.entries(banks)
                .sort(([, bankA], [, bankB]) => bankA.name.localeCompare(bankB.name))
                .map(([bankId, bankDetails]) => (
                  <ListGroup.Item key={bankId} disabled={redirecting} className="d-flex justify-content-between align-items-center" action onClick={() => connect(bankId)}>
                    <Bank id={bankId} />
                  </ListGroup.Item>
                ))}
            </ListGroup>
            : <Spinner animation="border" />}
        </Modal.Body>
      </Modal>

      <div>
        <ConnectionList />
      </div>

      <div className="text-center">
        <Button onClick={() => setShowBankSelection(true)} className="mt-2"><FontAwesomeIcon icon={faLink} /> New connection</Button>
      </div>

    </div>
  )
}

const ConnectionList: React.FC = () => {
  const axios = useAxios()
  const [connections, setConnections] = useState<Record<ConnectionId, ConnectionDetails>>({})
  const [loading, setLoading] = useState(true)

  const toggleSpendingLogSetting = async (connectionId: ConnectionId, accountId: AccountId) => {
    const accountDetails = connections[connectionId]?.accounts[accountId]

    if (accountDetails) {
      try {
        const response = await axios.put(`/v1/accounts/${accountDetails.iban}/features`, { spendingLog: !accountDetails.featureConfig.spendingLog, savings: accountDetails.featureConfig.savings })

        updateAccountFeatureConfig(connectionId, accountId, response.data)
      } catch (error) {
        console.error("Error toggling spending log setting", error)
      }
    } else {
      console.error(`Account details not found: ${connectionId}, ${accountId}`)
    }
  }

  const toggleSavingsSetting = async (connectionId: ConnectionId, accountId: AccountId) => {
    const accountDetails = connections[connectionId]?.accounts[accountId]

    if (accountDetails) {
      try {
        const response = await axios.put(`/v1/accounts/${accountDetails.iban}/features`, { spendingLog: accountDetails.featureConfig.spendingLog, savings: !accountDetails.featureConfig.savings })

        updateAccountFeatureConfig(connectionId, accountId, response.data)
      } catch (error) {
        console.error("Error toggling savings setting", error)
      }
    } else {
      console.error(`Account details not found: ${connectionId}, ${accountId}`)
    }
  }

  const updateAccountFeatureConfig = (connectionId: string, accountId: string, featureConfig: FeatureConfig) => {
    setConnections(previous => {
      return {
        ...previous,
        [connectionId]: {
          ...previous[connectionId],
          accounts: {
            ...previous[connectionId]?.accounts,
            [accountId]: { ...previous[connectionId]?.accounts[accountId], featureConfig }
          }
        }
      }
    })
  }

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        setLoading(true)
        const connectionsResponse = await axios.get(`/v1/connections`)
        console.log("Connections:", connectionsResponse.data.connections)
        setConnections(connectionsResponse.data.connections)
      } catch (error) {
        console.error("Error fetching connections", error)
      } finally {
        setLoading(false)
      }
    }

    fetchConnections()
  }, [axios])
  return <>
    {(loading) ? <div className="text-center my-3"><Spinner animation="border" /></div> : <div className="d-flex gap-2">
      {
        Object.entries(connections).map(([connectionId, connectionDetails]) => (
          <Card key={connectionId}>
            <Card.Body>
              <Card.Title><Bank id={connectionDetails.bankId} /></Card.Title>
              <Card.Subtitle>
                {connectionDetails.activatedAt ? Temporal.Instant.from(connectionDetails.activatedAt).toLocaleString() : "inactive"}
                <br/>
                {connectionDetails.expiresAt ? Temporal.Instant.from(connectionDetails.expiresAt).toLocaleString() : "inactive"}
              </Card.Subtitle>
              <Card.Text>{Object.entries(connectionDetails.accounts).map(([accountId, accountDetails]) => (
                <div key={accountId} className="d-flex flex-column gap-2">
                  <code>{accountDetails.iban}</code>
                  <span>{accountDetails.ownerName}</span>
                  <span>{accountDetails.displayName}</span>
                  <Badge bg="secondary">{accountDetails.currency}</Badge>
                  <div className="d-flex gap-2 mt-2">
                    <ToggleButton checked={accountDetails.featureConfig.spendingLog} variant="outline-primary" type="checkbox" size="sm" id={`${accountId}-spending-log`} onChange={() => toggleSpendingLogSetting(connectionId, accountId)} value="1">
                      <FontAwesomeIcon icon={faPiggyBank} /> Spending log
                    </ToggleButton>
                    <ToggleButton checked={accountDetails.featureConfig.savings} variant="outline-primary" type="checkbox" size="sm" id={`${accountId}-savings`} onChange={() => toggleSavingsSetting(connectionId, accountId)} value="1">
                      <FontAwesomeIcon icon={faWallet} /> Savings
                    </ToggleButton>
                  </div>
                </div>
              ))}</Card.Text>
            </Card.Body>
          </Card>
        ))

      }</div>

    }
  </>
}
